"use client"

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Clock, Users, Calendar, BookOpen, User, MapPin, CheckCircle, Home } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { YouTubeConsent } from "@/components/youtube-consent";
import { useRouter } from "next/navigation";

interface Course {
    id: string;
    title: string;
    subtitle: string;
    description: string;
    price: string;
    duration: string;
    classSize: string;
    startDate: string;
    schedule: string;
    frequency: string;
    teacher: string;
    image: string;
    slug: string;
    signupForm: string;
    highlighted: boolean;
    fullTitle: string;
    features: string[];
    video: string;
}

export default function CourseDetailPage({ params }: { params: { slug: string } }) {
    const router = useRouter();
    const [slug, setSlug] = useState<string>("");
    const [course, setCourse] = useState<Course | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const getSlug = async () => {
            const resolvedParams = await params;
            setSlug(resolvedParams.slug);
        };
        getSlug();
    }, [params]);

    useEffect(() => {
        if (!slug) return;

        const fetchCourse = async () => {
            try {
                setLoading(true);
                const response = await fetch('/api/courses');
                if (!response.ok) {
                    throw new Error('Failed to fetch courses');
                }
                const courses = await response.json();
                const foundCourse = courses.find((course: Course) => course.slug === slug);

                if (!foundCourse) {
                    setError('Course not found');
                    return;
                }

                setCourse(foundCourse);
            } catch (err) {
                console.error('Error fetching course:', err);
                setError('Failed to load course');
            } finally {
                setLoading(false);
            }
        };

        fetchCourse();
    }, [slug]);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Сургалтууд ачаалж байна...</p>
        </div>
      </div>
    );
  }

    if (error || !course) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-destructive mb-4">{error || 'Хичээл олдсонгүй'}</p>
                    <div className="space-x-4">
                        <Button onClick={() => router.back()}>
                            Буцах
                        </Button>
                        <Button onClick={() => window.location.reload()}>
                            Дахин оролдох
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            {/* Breadcrumb */}
            <section className="py-8 px-6">
                <div className="max-w-6xl mx-auto">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link href="/" className="flex items-center gap-1 text-foreground/90">
                                        <Home className="w-4 h-4" />
                                        Нүүр
                                    </Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link href="/courses" className="text-foreground/80">Сургалтууд</Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage className="text-foreground/90">{course.title}</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
            </section>

            {/* Header */}
            <section className="pb-8 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center">
                        {course.highlighted && (
                            <Badge className="bg-accent mb-4 p-2 text-base">
                                Онцлох хөтөлбөр
                            </Badge>
                        )}
                        <h1 className="text-3xl md:text-4xl   mb-4 text-foreground">{course.title}</h1>
                        <p className="text-base md:text-lg text-muted-foreground mb-8">{course.description}</p>
                    </div>
                </div>
            </section>

            {/* Course Details */}
            <section className="pb-12 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8">
                        {/* Mobile: Image First, Desktop: Right Column - Media */}
                        <div className="space-y-6 lg:order-2">
                            {/* Course Image */}
                            <Card className="rounded-3xl border shadow-lg overflow-hidden bg-card">
                                <Image
                                    src={course.image || 'https://mbg.mn/og.jpg'}
                                    alt={course.fullTitle}
                                    width={1000}
                                    height={1000}
                                    className="w-full h-auto object-cover"
                                />
                            </Card>

                            {/* Course Video (Click-to-load for privacy) */}
                            {course.video && (
                                <Card className="rounded-3xl border shadow-lg overflow-hidden bg-card">
                                    <CardContent className="p-6">
                                        <h3 className="text-xl   mb-4 text-foreground">Танилцуулга видео</h3>
                                        <div className="relative rounded-2xl overflow-hidden">
                                            <YouTubeConsent link={course.video} title="Course Introduction Video" className="rounded-2xl" />
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Enrollment Button */}
                            <Card className="rounded-3xl border shadow-lg bg-card">
                                <CardContent className="p-8 text-center">
                                    <h3 className="text-2xl   mb-4 text-foreground">Одоо бүртгүүлээрэй!</h3>
                                    <p className="text-muted-foreground mb-6">
                                        Хязгаарлагдмал суудлын тоо. Өөрийн байр авахыг хурдан болгоорой.
                                    </p>
                                    <Link href={course.signupForm} target="_blank">
                                        <Button
                                            size="lg"
                                            className="px-8 py-4 text-lg rounded-2xl    transform hover:scale-105 transition-all duration-300"
                                        >
                                            БҮРТГҮҮЛЭХ
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Mobile: Content Second, Desktop: Left Column - Course Info */}
                        <div className="space-y-6 lg:order-1">
                            {/* Main Course Info */}
                            <Card className="rounded-3xl border shadow-lg bg-card">
                                <CardContent className="p-8">
                                    <h2 className="text-2xl   mb-6 text-foreground">Хичээлийн мэдээлэл</h2>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-5 h-5 text-primary" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Эхлэх огноо</p>
                                                <p className="   text-foreground">Хичээл {course.startDate}-нээс эхлэх болно</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <Clock className="w-5 h-5 text-primary" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Нийт хичээлийн цаг</p>
                                                <p className="   text-foreground">{course.duration}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <BookOpen className="w-5 h-5 text-primary" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Хичээлийн хуваарь</p>
                                                <p className="   text-foreground">{course.schedule}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <MapPin className="w-5 h-5 text-primary" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Давтамж</p>
                                                <p className="   text-foreground">{course.frequency}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <Users className="w-5 h-5 text-primary" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Анги дүүргэлт</p>
                                                <p className="   text-foreground">{course.classSize}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-primary" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Багш</p>
                                                <p className="   text-foreground">{course.teacher}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-border">
                                        <div className="text-center">
                                            <p className="text-sm text-muted-foreground mb-2">Төлбөр</p>
                                            <p className="text-4xl   text-primary">{course.price}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Course Features */}
                            <Card className="rounded-3xl border shadow-lg bg-card">
                                <CardContent className="p-8">
                                    <h3 className="text-xl   text-foreground mb-4">Хичээлийн онцлогууд</h3>
                                    <div className="space-y-3">
                                        {course.features.map((feature, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                                                <span className="text-foreground">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}
